import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ExternalLink, Phone, Mail, MapPin } from 'lucide-react';
import { CATEGORIES } from '../lib/categoryConfig';
import BrandLogo from './BrandLogo';

const MAIN_SITE = 'https://klarthemma.com';
const PHONE = '0707 388 602';
const PHONE_HREF = 'tel:0707388602';
const EMAIL = 'kontakt@klarthemma.com';

const NAV_LINKS = [
  { label: 'Start',             to: '/' },
  { label: 'Om oss',            to: '/om-oss' },
  { label: 'Kontakt',           to: '/kontakt' },
  { label: 'Villkor',           to: '/villkor' },
  { label: 'Integritetspolicy', to: '/integritet' },
  { label: 'Cookies',           to: '/cookies' },
];

export default function AppFooter() {
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === '/';

  return (
    <footer className="kh-footer">
      {/* ── CTA block – startsidan only ────────────────────────────── */}
      {isHome && (
        <div className="kh-footer-cta">
          <p className="kh-eyebrow">Klart Hemma · Skåne</p>
          <h2>
            Har du ett projekt på gång?
          </h2>
          <p>
            Fråga oss om upplägget innan du skickar din förfrågan.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href={PHONE_HREF}
              className="kh-button kh-button--light px-7 py-3.5"
            >
              <Phone className="h-4 w-4 shrink-0" />
              {PHONE}
            </a>
            <a
              href={`mailto:${EMAIL}`}
              className="kh-footer-email inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/[0.3] font-semibold text-sm transition-colors"
            >
              <Mail className="h-4 w-4 shrink-0" />
              {EMAIL}
            </a>
          </div>
        </div>
      )}

      {/* ── Main grid (extra pt clears diagonal clip + aligns logo) ───── */}
      <div
        className={`kh-footer-inner grid sm:grid-cols-2 md:grid-cols-4 gap-10 ${
          isHome ? 'pt-16 sm:pt-20' : 'pt-24 sm:pt-32'
        }`}
      >

        {/* Brand */}
        <div>
          <BrandLogo className="kh-brand-image--footer" />
          <p className="kh-footer-muted text-xs leading-relaxed mb-5">
            Renovering och inredningsmontage i Skåne.
          </p>
        </div>

        {/* Tjänster */}
        <div>
          <h4 className="text-white/80 font-bold text-[10px] tracking-[0.18em] uppercase mb-5">
            Tjänster
          </h4>
          <ul className="space-y-2.5">
            {CATEGORIES.map((cat) => (
              <li key={cat.slug}>
                <button
                  type="button"
                  onClick={() => navigate(cat.route)}
                  className="text-slate-500 hover:text-white transition-colors text-sm text-left"
                >
                  {cat.label}
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Företaget */}
        <div>
          <h4 className="text-white/80 font-bold text-[10px] tracking-[0.18em] uppercase mb-5">
            Företaget
          </h4>
          <ul className="space-y-2.5">
            {NAV_LINKS.map(({ label, to }) => (
              <li key={to}>
                <button
                  type="button"
                  onClick={() => navigate(to)}
                  className="text-slate-500 hover:text-white transition-colors text-sm text-left"
                >
                  {label}
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Kontakt */}
        <div>
          <h4 className="text-white/80 font-bold text-[10px] tracking-[0.18em] uppercase mb-5">
            Kontakt
          </h4>
          <ul className="space-y-4 mb-6">
            <li>
              <a href={PHONE_HREF} className="flex items-center gap-3 group">
                <span className="h-8 w-8 rounded-lg bg-[#374ef4]/10 flex items-center justify-center shrink-0 group-hover:bg-[#374ef4]/20 transition-colors">
                  <Phone className="h-3.5 w-3.5 text-[#374ef4]" />
                </span>
                <span className="text-slate-400 group-hover:text-white transition-colors text-sm">
                  {PHONE}
                </span>
              </a>
            </li>
            <li>
              <a href={`mailto:${EMAIL}`} className="flex items-center gap-3 group">
                <span className="h-8 w-8 rounded-lg bg-[#374ef4]/10 flex items-center justify-center shrink-0 group-hover:bg-[#374ef4]/20 transition-colors">
                  <Mail className="h-3.5 w-3.5 text-[#374ef4]" />
                </span>
                <span className="text-slate-400 group-hover:text-white transition-colors text-sm break-all">
                  {EMAIL}
                </span>
              </a>
            </li>
            <li>
              <div className="flex items-center gap-3">
                <span className="h-8 w-8 rounded-lg bg-[#374ef4]/10 flex items-center justify-center shrink-0">
                  <MapPin className="h-3.5 w-3.5 text-[#374ef4]" />
                </span>
                <span className="text-slate-500 text-sm">Skåne</span>
              </div>
            </li>
          </ul>
          <a
            href={MAIN_SITE}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-[#374ef4] hover:text-[#f5f2e9] transition-colors text-xs font-semibold"
          >
            klarthemma.com
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </div>

      {/* ── Bottom bar ─────────────────────────────────────────────────── */}
      <div className="border-t border-white/[0.05] px-6 py-5">
          <p className="kh-footer-bottom max-w-7xl mx-auto text-center text-[11px]">
          © {new Date().getFullYear()} Klart Hemma AB · Alla rättigheter förbehållna
        </p>
      </div>
    </footer>
  );
}
